import classNames from 'classnames';
import { Ref, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CreateDonationRequestModeEnum, DonationMatching, Milestone, ProjectPhoto, Subproject } from '@/app/api';
import { Routes } from '@/app/enums';
import { PropsWithClassname } from '@/app/types';
import { envs } from '@/app/utils/envs';

import Button, { ButtonHTMLTag, ButtonStyle } from '@/app/components/Button/Button';
import DonateBoxCommercial from '@/app/components/DonateBoxCommercial/DonateBoxCommercial';
import DonateBoxCommercialFinished from '@/app/components/DonateBoxCommercialFinished/DonateBoxCommercialFinished';
import DonateBoxDobro from '@/app/components/DonateBoxDobro/DonateBoxDobro';
import ImageSwiper, { ImageSwiperCoverImage } from '@/app/components/ImageSwiper/ImageSwiper';
import { ImageCarousel } from '@/app/components/ProjectPageHeaderForAbTest/components/ImageCarousell';
import ProjectProgress from '@/app/components/ProjectPageHeaderForAbTest/components/ProjectProgress';
import { useIsProjectInEndState } from '@/app/providers/ProjectProvider';

export interface ProjectPageHeaderForAbTestProps extends PropsWithClassname {
  numericId?: number;
  isProjectLongTerm: boolean;
  title?: string;
  images: ProjectPhoto[];
  coverImage: ImageSwiperCoverImage;
  goalInCents?: number;
  currentAmountInCents?: number;
  donationsCount?: number;
  milestones?: Milestone[];
  endsAt?: Date;
  mainVideoSlug?: string;
  isUserAdmin?: boolean;
  onShareButtonClick: () => void;
  onVerificationNoteClick: () => void;
  innerRef?: Ref<HTMLDivElement>;
  subProject?: Subproject;
  isCommercial?: boolean;
  onCommercialDonateClick?: () => void;
  activeGoodHabitAmount?: number;
  matching?: DonationMatching;
  adminDetailLink?: string;
}

const ProjectPageHeaderForAbTest = ({
  numericId,
  images,
  isProjectLongTerm,
  title,
  coverImage,
  goalInCents,
  currentAmountInCents,
  milestones,
  endsAt,
  mainVideoSlug,
  innerRef,
  donationsCount,
  isUserAdmin,
  className,
  onShareButtonClick,
  onVerificationNoteClick,
  subProject,
  isCommercial,
  onCommercialDonateClick,
  activeGoodHabitAmount,
  matching,
  adminDetailLink,
}: ProjectPageHeaderForAbTestProps) => {
  const isProjectInEndState = useIsProjectInEndState();

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [selectedDonationMode, setSelectedDonationMode] = useState<CreateDonationRequestModeEnum>(
    CreateDonationRequestModeEnum.Single,
  );

  const donationModeOptions = useMemo(() => {
    return [
      { value: CreateDonationRequestModeEnum.Single, label: t('components.ProjectPageHeader.oneTimeDonation') },
      { value: CreateDonationRequestModeEnum.LongTerm, label: t('components.ProjectPageHeader.regularDonation') },
    ];
  }, [t]);

  const donateLinkQuery = useMemo(() => {
    const longTermQuery = selectedDonationMode === CreateDonationRequestModeEnum.LongTerm ? 'mode=long-term' : '';
    const subProjectQuery = subProject ? `subproject=${subProject.id}` : '';

    return `?${longTermQuery}${subProjectQuery}`;
  }, [isProjectLongTerm, selectedDonationMode, subProject]);

  const donateLink = useMemo(() => {
    const languageQuery = language === 'en' ? `lang=${language}` : '';

    if (activeGoodHabitAmount) {
      return `${envs.redirectUrl}/${Routes.USE_GOOD_HABIT}/${numericId}`;
    }

    return `/${Routes.DONATION}/${numericId}${donateLinkQuery}${languageQuery}`;
  }, [numericId, donateLinkQuery, activeGoodHabitAmount, language]);

  const isProjectProgressVisible = useMemo(() => {
    if (typeof currentAmountInCents !== 'number') {
      return false;
    }

    return true;
  }, [currentAmountInCents]);

  return (
    <div ref={innerRef} className={classNames(className)}>
      <ImageCarousel
        className="md:hidden"
        images={images}
        coverImage={coverImage}
        alt={title}
        mainVideoSlug={mainVideoSlug}
        endsAt={endsAt}
      />

      <div className="flex justify-between items-center">
        {!!title && (
          <h1
            data-testid="project-page-title"
            className="mb-[24px] md:mb-[40px] w-full max-w-[744px] inline-block text-[24px] lg:text-[48px] leading-[32px] lg:leading-[48px] font-medium text-neutral-dark"
          >
            {title}
          </h1>
        )}
        {isUserAdmin && adminDetailLink && (
          <Button
            tagSpecificProps={{
              htmlTag: ButtonHTMLTag.LINK,
              // href: `${envs.redirectUrl}/${Routes.PROJECT_ADMIN}/${id}`,
              href: adminDetailLink,
            }}
            style={ButtonStyle.PRIMARY_OUTLINED}
            className="!tracking-[1px]"
          >
            {t('components.ProjectPageHeader.projectAdministrationLink')}
          </Button>
        )}
      </div>
      <div className="flex flex-col lg:flex-row">
        <div className="flex flex-col lg:grow">
          {isProjectProgressVisible && (
            <ProjectProgress
              isCurrentAmountPrimaryColor={!subProject}
              matching={matching}
              totalInCents={goalInCents}
              currentValueInCents={currentAmountInCents!}
              milestones={milestones}
              endsAt={endsAt}
              donationsCount={donationsCount}
              className="lg:hidden p-[0px]"
            />
          )}
          <ImageSwiper
            className="hidden md:flex"
            images={images}
            coverImage={coverImage}
            alt={title}
            mainVideoSlug={mainVideoSlug}
          />
        </div>
        <div className="lg:w-[360px] lg:ml-[24px]">
          {isProjectProgressVisible && (
            <ProjectProgress
              isCurrentAmountPrimaryColor={!subProject}
              matching={matching}
              totalInCents={goalInCents}
              currentValueInCents={currentAmountInCents!}
              milestones={milestones}
              endsAt={endsAt}
              donationsCount={donationsCount}
              testId="desktop-progress-bar"
              className="hidden lg:block mb-[24px] p-[0px]"
            />
          )}
          {!isProjectInEndState && !!numericId && !isCommercial && (
            <DonateBoxDobro
              heading={t('components.DonateBox.headingAbTest')}
              subProject={subProject}
              donateLink={donateLink}
              onShareButtonClick={onShareButtonClick}
              onVerificationNoteClick={onVerificationNoteClick}
              donationModeOptions={donationModeOptions}
              selectedDonationMode={selectedDonationMode}
              onChangeSelectedDonationMode={setSelectedDonationMode}
              isProjectLongTerm={isProjectLongTerm}
              activeGoodHabitAmount={activeGoodHabitAmount}
              matching={matching}
            />
          )}
          {!isProjectInEndState && !!numericId && isCommercial && onCommercialDonateClick && (
            <DonateBoxCommercial
              onDonateClick={onCommercialDonateClick}
              onShareButtonClick={onShareButtonClick}
              donationModeOptions={donationModeOptions}
              selectedDonationMode={selectedDonationMode}
              onChangeSelectedDonationMode={setSelectedDonationMode}
              isProjectLongTerm={isProjectLongTerm}
            />
          )}
          {isProjectInEndState && !!numericId && isCommercial && (
            <DonateBoxCommercialFinished
              onShowRewardsClick={onCommercialDonateClick}
              onShareButtonClick={onShareButtonClick}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectPageHeaderForAbTest;
